/**
 * Men's Health Intake Page
 * 
 * Comprehensive TRT-focused intake form for male patients
 * Ported and adapted from Peptide Ledger with NovaMed branding
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  Shield,
  Zap,
  Activity,
  Heart,
  Moon,
  Brain,
  Flame,
  Dumbbell,
  Clock,
  User,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { format, parse, isValid } from 'date-fns';
import { getIntakePrefillData } from '@/services/intakeService';
import { mapPrefillToMensIntake, type MensIntakeFormData } from '@/lib/intake/prefillMapper';
import ShippingBillingAddressStep, { isShippingBillingComplete } from '@/components/intake/ShippingBillingAddressStep';
import PaymentStep from '@/components/intake/shared/PaymentStep';
import AllergyMultiSelect from '@/components/ui/allergy-multi-select';
import MedicalHistoryMultiSelect from '@/components/ui/medical-history-multi-select';
import IntakeBookingStep from '@/components/intake/shared/IntakeBookingStep';
import { CalendarButtons } from '@/components/booking/CalendarButtons';
import { useGuidedIntakeStepFlow } from '@/hooks/useGuidedIntakeStepFlow';
import type { GuidedPaymentStatus } from '@/types/guidedOrder';

// Step indices for the TRT intake. Base flow ends at REVIEW_STEP; guided
// orders append PAYMENT_STEP for Stripe authorization before submit.
// Nimbus-fulfilled orders insert BOOKING_STEP between Review and Payment so
// that payment success bundles intake submission + appointment creation.
const REVIEW_STEP = 8;
const BOOKING_STEP = 9;
const PAYMENT_STEP = 10;

// ============================================================================
// TYPES
// ============================================================================

interface MensFormData {
  // Step 1: Goals
  selectedGoals: string[];
  
  // Step 2: Personal Info
  fullName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  heightFeet: number | null;
  heightInches: number | null;
  weight: string;
  state: string;
  
  // Step 3: ADAM Questionnaire
  adam: Record<string, boolean>;
  
  // Step 4: STOP-Bang Questionnaire
  stopBang: Record<string, boolean>;
  
  // Step 5: Medical History
  currentMedications: string;
  allergies: string[];
  previousTRT: string;
  previousTRTDetails: string;
  chronicConditions: string[];
  chronicConditionsOther: string;
  surgeries: string;
  familyHistory: string[];
  
  // Step 6: Lifestyle
  activityLevel: string;
  exerciseFrequency: string;
  sleepHours: string;
  sleepQuality: string;
  stressLevel: string;
  alcoholConsumption: string;
  tobaccoUse: string;
  
  // SMS Consent
  smsConsent: boolean;

  // Step 7: Shipping & Billing Address
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingSameAsShipping: boolean;

  // Step 8: Review
  consentAccurate: boolean;
  consentMonitoring: boolean;
  consentTerms: boolean;
}

const initialFormData: MensFormData = {
  selectedGoals: [],
  fullName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  heightFeet: null,
  heightInches: null,
  weight: '',
  state: '',
  adam: {},
  stopBang: {},
  currentMedications: '',
  allergies: [],
  previousTRT: '',
  previousTRTDetails: '',
  chronicConditions: [],
  chronicConditionsOther: '',
  surgeries: '',
  familyHistory: [],
  activityLevel: '',
  exerciseFrequency: '',
  sleepHours: '',
  sleepQuality: '',
  stressLevel: '',
  alcoholConsumption: '',
  tobaccoUse: '',
  smsConsent: true,
  shippingAddressLine1: '',
  shippingAddressLine2: '',
  shippingCity: '',
  shippingState: '',
  shippingPostalCode: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  billingSameAsShipping: true,
  consentAccurate: false,
  consentMonitoring: false,
  consentTerms: false,
};

// ============================================================================
// CONSTANTS
// ============================================================================

const MENS_GOALS = [
  { id: 'energy', label: 'Boost energy levels', description: 'Combat fatigue and increase vitality', icon: Zap },
  { id: 'libido', label: 'Improve libido/sexual health', description: 'Enhance desire and performance', icon: Heart },
  { id: 'strength', label: 'Build strength/muscle', description: 'Improve body composition', icon: Dumbbell },
  { id: 'mood', label: 'Stabilize mood', description: 'Better emotional balance', icon: Brain },
  { id: 'sleep', label: 'Improve sleep quality', description: 'More restful, restorative sleep', icon: Moon },
  { id: 'focus', label: 'Sharpen mental clarity', description: 'Better focus and cognition', icon: Activity },
  { id: 'weight', label: 'Metabolic support', description: 'Weight management and metabolism', icon: Flame },
];

const ADAM_QUESTIONS = [
  { id: 'q1_decreased_libido', text: 'Do you have a decrease in libido (sex drive)?' },
  { id: 'q2_lack_of_energy', text: 'Do you have a lack of energy?' },
  { id: 'q3_decreased_strength', text: 'Do you have a decrease in strength and/or endurance?' },
  { id: 'q4_lost_height', text: 'Have you lost height?' },
  { id: 'q5_decreased_enjoyment', text: 'Have you noticed a decreased enjoyment of life?' },
  { id: 'q6_sad_or_grumpy', text: 'Are you sad and/or grumpy?' },
  { id: 'q7_weaker_erections', text: 'Are your erections less strong?' },
  { id: 'q8_sports_performance_decline', text: 'Have you noticed a recent deterioration in your ability to play sports?' },
  { id: 'q9_falling_asleep_after_dinner', text: 'Are you falling asleep after dinner?' },
  { id: 'q10_work_performance_decline', text: 'Has there been a recent deterioration in your work performance?' },
];

const STOP_BANG_QUESTIONS = [
  { id: 'snore', text: 'Do you snore loudly (louder than talking or loud enough to be heard through closed doors)?' },
  { id: 'tired', text: 'Do you often feel tired, fatigued, or sleepy during the daytime?' },
  { id: 'observed_apneas', text: 'Has anyone observed you stop breathing during your sleep?' },
  { id: 'high_blood_pressure', text: 'Do you have or are you being treated for high blood pressure?' },
  { id: 'bmi_over_35', text: 'Is your BMI more than 35 kg/m²?' },
  { id: 'neck_over_40cm', text: 'Is your neck circumference greater than 40 cm (16 inches)?' },
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const STATE_ABBR_TO_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

const normalizeStateValue = (state?: string): string => {
  const raw = (state || '').trim();
  if (!raw) return '';
  if (US_STATES.includes(raw)) return raw;
  const upper = raw.toUpperCase();
  if (STATE_ABBR_TO_NAME[upper]) return STATE_ABBR_TO_NAME[upper];
  const byCaseInsensitiveName = US_STATES.find(s => s.toLowerCase() === raw.toLowerCase());
  return byCaseInsensitiveName || '';
};

const normalizeCsvOrArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const parseHeightToFeetInches = (value?: string | number | null): { heightFeet: number | null; heightInches: number | null } => {
  if (value === null || value === undefined) return { heightFeet: null, heightInches: null };
  const raw = String(value).trim();
  if (!raw) return { heightFeet: null, heightInches: null };

  const feetInchesMatch = raw.match(/^\s*(\d{1,2})\s*(?:'|ft)\s*(\d{1,2})\s*(?:"|in)?\s*$/i);
  if (feetInchesMatch) {
    const feet = parseInt(feetInchesMatch[1], 10);
    const inches = parseInt(feetInchesMatch[2], 10);
    if (Number.isNaN(feet) || Number.isNaN(inches) || feet < 0 || inches < 0) {
      return { heightFeet: null, heightInches: null };
    }
    const totalInches = feet * 12 + inches;
    return { heightFeet: Math.floor(totalInches / 12), heightInches: totalInches % 12 };
  }

  const numeric = parseFloat(raw.replace(/[^\d.]/g, ''));
  if (Number.isNaN(numeric) || !Number.isFinite(numeric) || numeric <= 0) {
    return { heightFeet: null, heightInches: null };
  }

  const totalInches = numeric <= 96 ? Math.round(numeric) : Math.round(numeric / 2.54);
  return { heightFeet: Math.floor(totalInches / 12), heightInches: totalInches % 12 };
};

const normalizeMensPrefill = (prefill: Partial<MensFormData> & { height?: string | number | null }): Partial<MensFormData> => {
  const { height, ...rest } = prefill;
  const hasFeetInches = rest.heightFeet !== undefined && rest.heightFeet !== null
    || rest.heightInches !== undefined && rest.heightInches !== null;
  const parsed = hasFeetInches
    ? { heightFeet: rest.heightFeet ?? null, heightInches: rest.heightInches ?? null }
    : parseHeightToFeetInches(height);
  return {
    ...rest,
    heightFeet: parsed.heightFeet,
    heightInches: parsed.heightInches,
    state: normalizeStateValue(rest.state),
    allergies: normalizeCsvOrArray(rest.allergies as string | string[] | undefined),
    chronicConditions: normalizeCsvOrArray(rest.chronicConditions),
    familyHistory: normalizeCsvOrArray(rest.familyHistory),
  };
};

const FAMILY_HISTORY_OPTIONS = [
  'Prostate Cancer', 'Breast Cancer', 'Blood Clots', 'Heart Disease',
  'Stroke', 'Diabetes', 'None of the above'
];

// ============================================================================
// LANDING PAGE COMPONENT
// ============================================================================

export const MensIntakeLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [intakeData, setIntakeData] = useState<any>(null);
  const [clinicName, setClinicName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      // Demo tokens for testing
      if (token?.startsWith('demo') || token?.startsWith('intake-')) {
        setIsValid(true);
        setIntakeData({
          patientName: 'Demo Patient',
          providerName: 'Dr. Demo Provider',
          intakeType: "Men's Health (TRT)",
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        });
        setClinicName('Demo Clinic');
        setIsValidating(false);
        return;
      }

      try {
        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const data = await response.json();

        if (data.success) {
          // Check if therapy category matches - redirect to correct form if needed
          const therapyCategory = data.data?.therapyCategory || data.therapyCategory;
          if (therapyCategory && therapyCategory !== 'trt') {
            // Redirect to the correct intake form
            if (therapyCategory === 'hrt') {
              navigate(`/patient/intake/womens/${token}`, { replace: true });
              return;
            } else {
              // General or other therapy types
              navigate(`/patient/intake/${token}`, { replace: true });
              return;
            }
          }
          
          setIsValid(true);
          setIntakeData(data.data);
          setClinicName(data.data?.clinicName || data.data?.clinic?.name || '');
        } else {
          setIsValid(false);
          setError(data.message || data.error || 'This intake link is invalid or has expired.');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setIsValid(false);
        setError('Unable to validate intake link. Please try again.');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleStartIntake = () => {
    navigate(`/patient/intake/mens/${token}/form`);
  };

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Validating your link...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#0A0F1A]">
        <div className="text-center max-w-md bg-[#1a2332] rounded-2xl shadow-lg border border-white/10 p-8">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-light mb-2 text-white font-montserrat">
            {error?.includes('cancelled') ? 'Link Cancelled' : 'Link Invalid'}
          </h2>
          <p className="mb-4 text-white/60">
            {error || 'This intake link is invalid or has expired.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider for a new link.
          </p>
        </div>
      </div>
    );
  }

  const expiresAt = intakeData?.expiresAt ? new Date(intakeData.expiresAt) : null;
  const daysUntilExpiry = expiresAt 
    ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 7;
  const brandTitle = clinicName || 'Nimbus Providers';
  const clinicBrandLine = clinicName ? 'Partnered with Nimbus Providers' : "Men's Health Intake";

  return (
    <div className="min-h-screen bg-[#0A0F1A] intake-dark-form">
      {/* Header */}
      <header className="py-6 px-6 bg-[#0A0F1A]/95 border-b border-white/10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#4A8A9A]/10">
            <Dumbbell className="w-5 h-5 text-[#4A8A9A]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-montserrat">{brandTitle}</h1>
            <p className="text-xs text-white/90">{clinicBrandLine}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Provider Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-[#4A8A9A]/10 border border-[#4A8A9A]/20">
            <User className="w-4 h-4 text-[#4A8A9A]" />
            <span className="text-sm text-[#4A8A9A]">
              Requested by {intakeData?.providerName ? `Dr. ${intakeData.providerName}` : ''}
            </span>
          </div>

          {/* Hero */}
          <h2 className="text-4xl md:text-5xl font-light mb-4 text-white font-montserrat">
            Welcome, <span className="text-[#4A8A9A] italic">{intakeData?.patientName?.split(' ')[0] || 'Patient'}!</span>
          </h2>
          <p className="text-lg mb-8 max-w-2xl text-white/60">
            This comprehensive questionnaire will help us understand your health goals and create a personalized testosterone optimization plan tailored to your needs.
          </p>

          {/* Expiration Warning */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg mb-8 inline-flex bg-amber-50 border border-amber-200">
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-600">
              This link expires in {daysUntilExpiry} days
            </span>
          </div>

          {/* What to Expect */}
          <div className="rounded-2xl p-8 mb-8 bg-white/5 border-white/10">
            <h3 className="text-xl font-semibold mb-6 text-white font-montserrat">What to Expect</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-[#4A8A9A]/10">
                  <span className="text-xl font-bold text-[#4A8A9A]">7</span>
                </div>
                <h4 className="font-medium mb-1 text-white">Steps</h4>
                <p className="text-sm text-white/60">
                  Comprehensive assessment
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-[#4A8A9A]/10">
                  <span className="text-xl font-bold text-[#4A8A9A]">15-20</span>
                </div>
                <h4 className="font-medium mb-1 text-white">Minutes</h4>
                <p className="text-sm text-white/60">
                  Estimated completion time
                </p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 bg-[#4A8A9A]/10">
                  <CheckCircle className="w-6 h-6 text-[#4A8A9A]" />
                </div>
                <h4 className="font-medium mb-1 text-white">Auto-Save</h4>
                <p className="text-sm text-white/60">
                  Your progress is saved
                </p>
              </div>
            </div>
          </div>

          {/* Steps Overview */}
          <div className="rounded-2xl p-8 mb-8 bg-white/5 border-white/10">
            <h3 className="text-xl font-semibold mb-6 text-white font-montserrat">Steps Overview</h3>
            <div className="space-y-4">
              {[
                { step: 1, title: 'Health Goals', desc: 'What you want to achieve' },
                { step: 2, title: 'Personal Information', desc: 'Basic details and contact info' },
                { step: 3, title: 'ADAM Questionnaire', desc: 'Validated low-T screening tool' },
                { step: 4, title: 'Sleep Apnea Screening', desc: 'STOP-Bang questionnaire' },
                { step: 5, title: 'Medical History', desc: 'Medications, conditions, allergies' },
                { step: 6, title: 'Lifestyle', desc: 'Activity, sleep, and habits' },
                { step: 7, title: 'Review & Consent', desc: 'Confirm your responses' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#4A8A9A]/10">
                    <span className="text-sm font-semibold text-[#4A8A9A]">{step}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{title}</h4>
                    <p className="text-sm text-white/60">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleStartIntake}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:scale-105 bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A] text-white shadow-lg"
          >
            Begin Assessment
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Security Notice */}
          <div className="flex items-center gap-2 mt-6 text-white/60">
            <Shield className="w-4 h-4" />
            <span className="text-sm">Your information is securely encrypted and HIPAA compliant</span>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================================
// FORM COMPONENT
// ============================================================================

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MensIntakeForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<MensFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [clinicName, setClinicName] = useState<string>('');
  const [providerName, setProviderName] = useState<string>('');
  // Payment is skipped when the hold was already placed / captured
  // (e.g. via storefront checkout) — see shouldSkip inside PaymentStep.
  const [isGuided, setIsGuided] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<GuidedPaymentStatus | null>(null);
  const [isNimbusBookingEligible, setIsNimbusBookingEligible] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState<{
    id: string; start_time: string; end_time: string; duration: number;
    telehealthLink?: string | null;
  } | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{
    slot_start: string; slot_end: string; timezone: string;
  } | null>(null);
  const showPayment = isGuided && paymentStatus !== 'hold_placed' && paymentStatus !== 'captured';
  const showBooking = isNimbusBookingEligible && showPayment;
  const { activeStepIndices, totalSteps, stepPosition } = useGuidedIntakeStepFlow({
    baseStepCount: REVIEW_STEP,
    bookingStep: BOOKING_STEP,
    paymentStep: PAYMENT_STEP,
    showBooking,
    showPayment,
    currentStep,
  });
  const brandTitle = clinicName || 'Nimbus Providers';
  const clinicBrandLine = clinicName ? 'Partnered with Nimbus Providers' : "Men's Health Intake";

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStep]);

  const validateEmail = (email: string): string | null => {
    if (!email?.trim()) {
      return 'Email address is required';
    }
    if (!EMAIL_REGEX.test(email)) {
      return 'Please enter a valid email (e.g., name@example.com)';
    }
    return null;
  };

  const handleEmailChange = (email: string) => {
    updateFormData({ email });
    if (emailTouched) {
      setEmailError(validateEmail(email));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(formData.email));
  };

  // Validate token on mount to check if cancelled and fetch prefill data
  useEffect(() => {
    const validateToken = async () => {
      if (!token || token.startsWith('demo') || token.startsWith('intake-')) {
        if (token?.startsWith('demo') || token?.startsWith('intake-')) {
          setClinicName('Demo Clinic');
        }
        setIsValidating(false);
        setInitialLoadComplete(true);
        return;
      }

      try {
        // First check if there's existing draft data
        const savedData = sessionStorage.getItem(`mens-intake-${token}`);
        const hasDraft = savedData && savedData !== JSON.stringify(initialFormData);

        const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        const response = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const data = await response.json();
        
        if (!data.success) {
          setIsCancelled(true);
          setCancelledMessage(data.message || data.error || 'This intake link is no longer valid.');
          // Clear saved form data
          sessionStorage.removeItem(`mens-intake-${token}`);
        } else {
          setClinicName(data.data?.clinicName || data.data?.clinic?.name || '');
          setProviderName(data.data?.providerName || '');
          setIsGuided(Boolean(data.data?.isGuided));
          setPaymentStatus((data.data?.paymentStatus ?? null) as GuidedPaymentStatus | null);
          setIsNimbusBookingEligible(Boolean(data.data?.isNimbusBookingEligible));
          // Fetch prefill data after validation succeeds
          try {
            const prefillResult = await getIntakePrefillData(token);
            if (prefillResult.success && prefillResult.data) {
              const mappedPrefill = normalizeMensPrefill(mapPrefillToMensIntake(prefillResult.data));
              
              if (hasDraft) {
                // Merge prefill with draft (draft takes priority)
                const draftData = JSON.parse(savedData) as Partial<MensFormData>;
                const normalizedMerged = normalizeMensPrefill({
                  ...mappedPrefill,
                  ...draftData,
                });
                setFormData({
                  ...initialFormData,
                  ...normalizedMerged,
                  state: normalizeStateValue(normalizedMerged.state),
                });
              } else {
                // No draft - use prefill as base
                const normalizedPrefill = normalizeMensPrefill(mappedPrefill);
                setFormData({
                  ...initialFormData,
                  ...normalizedPrefill,
                  state: normalizeStateValue(normalizedPrefill.state),
                });
              }
            }
          } catch (error) {
            console.error('Error fetching prefill data:', error);
            // Load draft if available, otherwise use initial data
            if (hasDraft) {
              const draftData = JSON.parse(savedData) as Partial<MensFormData>;
              const normalizedDraft = normalizeMensPrefill(draftData);
              setFormData({
                ...initialFormData,
                ...normalizedDraft,
                state: normalizeStateValue(normalizedDraft.state),
              });
            }
          }
        }
      } catch (error) {
        console.error('Token validation error:', error);
      } finally {
        setIsValidating(false);
        setInitialLoadComplete(true);
      }
    };

    validateToken();
  }, [token]);

  // Check if already submitted on mount
  useEffect(() => {
    const submittedStatus = sessionStorage.getItem(`mens-intake-submitted-${token}`);
    if (submittedStatus === 'true') {
      setIsSubmitted(true); // Show success page
    }
  }, [token]);

  // Note: Draft loading is now handled in the validation effect above
  // to ensure proper merging with prefill data

  // Auto-save on change (only after initial load to avoid race condition with prefill)
  useEffect(() => {
    if (initialLoadComplete) {
      sessionStorage.setItem(`mens-intake-${token}`, JSON.stringify(formData));
    }
  }, [formData, token, initialLoadComplete]);

  const updateFormData = (updates: Partial<MensFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const calculateAdamScore = () => {
    return Object.values(formData.adam).filter(v => v === true).length;
  };

  const calculateStopBangScore = () => {
    let score = Object.values(formData.stopBang).filter(v => v === true).length;
    // Add age > 50 and male (always true for this form)
    if (formData.dateOfBirth) {
      const age = new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear();
      if (age > 50) score++;
    }
    score++; // Male = 1 point
    return score;
  };

  const handleSubmit = async (payment?: { paymentMethodId: string; setupIntentId: string }) => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      // Re-validate the intake status before submission
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

      // Skip validation for demo tokens
      if (!token?.startsWith('demo') && !token?.startsWith('intake-')) {
        const validateResponse = await fetch(`${apiUrl}/intakes/validate/${token}`);
        const validateData = await validateResponse.json();

        if (!validateData.success) {
          setIsCancelled(true);
          setCancelledMessage(validateData.message || 'This intake link is no longer valid.');
          sessionStorage.removeItem(`mens-intake-${token}`);
          setIsSubmitting(false);
          return;
        }
      }

      const scores = {
        adam: calculateAdamScore(),
        stopBang: calculateStopBangScore(),
      };

      const response = await fetch(`${apiUrl}/intakes/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          responses: formData,
          scores,
          paymentMethodId: payment?.paymentMethodId,
          setupIntentId: payment?.setupIntentId,
          appointmentSlot: selectedSlot || undefined,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        sessionStorage.setItem(`mens-intake-submitted-${token}`, 'true');
        sessionStorage.removeItem(`mens-intake-${token}`);
        if (data.data?.appointment) {
          setBookedAppointment(data.data.appointment);
        }
        setIsSubmitted(true);
      } else {
        // Check if intake was cancelled or expired
        if (data.message?.includes('cancelled') || data.message?.includes('expired')) {
          setIsCancelled(true);
          setCancelledMessage(data.message);
          sessionStorage.removeItem(`mens-intake-${token}`);
        } else {
          setSubmissionError(data.message || 'Failed to submit intake. Please try again.');
        }
      }
    } catch (err) {
      console.error('Submission error:', err);
      setSubmissionError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    const hasValidState = US_STATES.includes(normalizeStateValue(formData.state));
    switch (currentStep) {
      case 1: return formData.selectedGoals.length > 0;
      case 2:
        return Boolean(
          formData.fullName?.trim()
          && formData.email?.trim()
          && EMAIL_REGEX.test(formData.email)
          && formData.dateOfBirth
          && hasValidState
        );
      case 3: return ADAM_QUESTIONS.every(q => formData.adam[q.id] !== undefined);
      case 4: return STOP_BANG_QUESTIONS.every(q => formData.stopBang[q.id] !== undefined);
      case 5: return true;
      case 6: return formData.activityLevel && formData.sleepQuality;
      case 7: {
        const billingComplete = Boolean(
          formData.billingAddressLine1?.trim() &&
          formData.billingCity?.trim() &&
          formData.billingState?.trim() &&
          formData.billingPostalCode?.trim() &&
          /^\d{5}(-\d{4})?$/.test(formData.billingPostalCode.trim())
        );
        if (!billingComplete) return false;
        if (!formData.billingSameAsShipping) {
          return Boolean(
            formData.shippingAddressLine1?.trim() &&
            formData.shippingCity?.trim() &&
            formData.shippingState?.trim() &&
            formData.shippingPostalCode?.trim() &&
            /^\d{5}(-\d{4})?$/.test(formData.shippingPostalCode.trim())
          );
        }
        return true;
      }
      case 8: return formData.consentAccurate && formData.consentMonitoring && formData.consentTerms;
      default: return false;
    }
  };

  const baseSteps = ['Goals', 'Personal Info', 'ADAM', 'Sleep Apnea', 'Medical', 'Lifestyle', 'Address', 'Review'];
  const steps = [...baseSteps];
  if (showBooking) steps.push('Book Appointment');
  if (showPayment) steps.push('Payment');

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1A]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#4A8A9A]" />
          <p className="text-white/60">Loading your intake form...</p>
        </div>
      </div>
    );
  }

  // Show cancelled message if intake was cancelled
  if (isCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#0A0F1A]">
        <div className="text-center max-w-md bg-[#1a2332] rounded-2xl shadow-lg border border-white/10 p-8">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500/10">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-light mb-2 text-white font-montserrat">
            {cancelledMessage?.includes('cancelled') ? 'Intake Cancelled' : 'Link No Longer Valid'}
          </h2>
          <p className="mb-4 text-white/60">
            {cancelledMessage || 'This intake form is no longer available.'}
          </p>
          <p className="text-sm text-white/40">
            Please contact your healthcare provider if you need to complete a new intake.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (isSubmitted) {
    const providerLabel = providerName ? `Dr. ${providerName}` : 'your provider';
    const formattedDateTime = bookedAppointment
      ? `${new Date(bookedAppointment.start_time).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })} at ${new Date(bookedAppointment.start_time).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}`
      : null;
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#0A0F1A]">
        <div className="text-center max-w-md bg-white rounded-2xl shadow-lg border p-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#4A8A9A]/10">
            <CheckCircle className="w-10 h-10 text-[#4A8A9A]" />
          </div>
          <h2 className="text-3xl font-light mb-4 text-gray-900 font-montserrat">
            Intake <span className="text-[#4A8A9A] italic">Complete!</span>
          </h2>
          <p className="mb-2 text-gray-600">
            Thank you for completing your health questionnaire.
          </p>
          {formattedDateTime ? (
            <p className="mb-6 text-gray-600">
              <span className="font-semibold">Note:</span> Please join your video call on{' '}
              <span className="font-semibold">{formattedDateTime}</span>. You'll receive a
              confirmation email with your video visit link.
            </p>
          ) : (
            <p className="mb-6 text-gray-600">
              Your provider will review your responses and prepare recommendations shortly.
            </p>
          )}
          {bookedAppointment && (
            <div className="rounded-xl p-6 text-left bg-[#4A8A9A]/10 border border-[#4A8A9A]/30">
              <h4 className="font-semibold mb-2 text-[#4A8A9A]">Your Appointment</h4>
              <p className="text-sm text-gray-700 mb-4">{formattedDateTime}</p>
              <CalendarButtons
                appointment={{
                  start_time: bookedAppointment.start_time,
                  end_time: bookedAppointment.end_time,
                  title: `Telehealth Visit with ${providerLabel}`,
                  description: clinicBrandLine || undefined,
                  location:
                    bookedAppointment.telehealthLink || clinicBrandLine || undefined,
                }}
                practitionerName={providerLabel}
                clinicName={clinicBrandLine || undefined}
                telehealthLink={bookedAppointment.telehealthLink ?? undefined}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto" style={{ background: '#0a0f1a' }}>
      {/* Header */}
      <header className="py-4 px-6 border-b sticky top-0 z-10" style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: '#0a0f1a' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-[#4A8A9A]" />
            <div>
              <p className="font-semibold text-white">{brandTitle}</p>
              <p className="text-xs text-white/60">{clinicBrandLine}</p>
            </div>
          </div>
          <span className="text-sm text-white/60">
            Step {stepPosition} of {totalSteps}
          </span>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="h-1 sticky top-[73px] z-10" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
        <div
          className="h-full transition-all duration-300 bg-gradient-to-r from-[#4A8A9A] to-[#4A8A9A]"
          style={{ width: `${(stepPosition / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step Tabs */}
      <div className="py-4 px-6 border-b overflow-x-auto sticky top-[74px] z-10" style={{ borderColor: 'rgba(255, 255, 255, 0.05)', background: '#0a0f1a' }}>
        <div className="max-w-4xl mx-auto flex gap-2">
          {steps.map((step, idx) => {
            const stepNum = activeStepIndices[idx];
            const isCurrent = currentStep === stepNum;
            const currentIdx = activeStepIndices.indexOf(currentStep);
            const isPast = currentIdx > idx;
            return (
              <button
                key={step}
                onClick={() => isPast && setCurrentStep(stepNum)}
                disabled={!isPast && !isCurrent}
                className="px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all"
                style={{
                  background: isCurrent ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  color: isCurrent ? '#3B82F6' : isPast ? '#2FFFC7' : 'rgba(255, 255, 255, 0.3)',
                  border: isCurrent ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
                }}
              >
                {isPast && <CheckCircle className="w-3 h-3 inline mr-1" />}
                {step}
              </button>
            );
          })}
        </div>
      </div>

      {/* Form Content */}
      <main className="py-8 px-6 pb-20">
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Goals */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                What are your health <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>goals?</span>
              </h2>
              <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Select all that apply to you
              </p>
              <div className="space-y-3">
                {MENS_GOALS.map(goal => {
                  const Icon = goal.icon;
                  const isSelected = formData.selectedGoals.includes(goal.id);
                  return (
                    <button
                      key={goal.id}
                      onClick={() => {
                        const newGoals = isSelected
                          ? formData.selectedGoals.filter(g => g !== goal.id)
                          : [...formData.selectedGoals, goal.id];
                        updateFormData({ selectedGoals: newGoals });
                      }}
                      className="w-full p-4 rounded-xl text-left transition-all flex items-center gap-4"
                      style={{
                        background: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                        border: isSelected ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ background: isSelected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)' }}
                      >
                        <Icon className="w-6 h-6" style={{ color: isSelected ? '#3B82F6' : 'rgba(255, 255, 255, 0.4)' }} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium" style={{ color: '#E6E9F0' }}>{goal.label}</h4>
                        <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{goal.description}</p>
                      </div>
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ 
                          background: isSelected ? '#3B82F6' : 'rgba(255, 255, 255, 0.1)',
                          border: isSelected ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        {isSelected && <CheckCircle className="w-4 h-4" style={{ color: '#fff' }} />}
                      </div>
                    </button>
                  );
                })}
              </div>
              {formData.selectedGoals.length === 0 && (
                <p className="text-sm mt-4" style={{ color: '#f97316' }}>
                  Please select at least one goal to continue
                </p>
              )}
            </div>
          )}

          {/* Step 2: Personal Info */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                Personal <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>Information</span>
              </h2>
              <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Tell us about yourself
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Full Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={e => updateFormData({ fullName: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                    placeholder="John Doe"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Email <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      className="w-full px-4 py-3 rounded-lg"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        border: emailError && emailTouched ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.1)', 
                        color: '#E6E9F0' 
                      }}
                      placeholder="john@example.com"
                    />
                    {emailError && emailTouched && (
                      <p className="mt-1 text-xs text-red-400">{emailError}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => updateFormData({ phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg"
                      style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                      placeholder="(555) 123-4567"
                    />

                    {/* SMS Consent */}
                    <button
                      onClick={() => updateFormData({ smsConsent: !formData.smsConsent })}
                      className="w-full mt-3 p-3 rounded-lg text-left flex items-start gap-3 transition-all"
                      style={{
                        background: formData.smsConsent ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                        border: formData.smsConsent ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{
                          background: formData.smsConsent ? '#2FFFC7' : 'rgba(255, 255, 255, 0.1)',
                          border: formData.smsConsent ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        {formData.smsConsent && <CheckCircle className="w-3 h-3" style={{ color: '#000' }} />}
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm block" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          I agree to receive text messages from Nimbus Providers
                        </span>
                        <span className="text-xs block leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          Including prescription updates, refill reminders, and order status notifications.
                          Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to cancel. Reply HELP for help.
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Date of Birth <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <DatePicker
                    value={formData.dateOfBirth ? (() => {
                      const parsedDate = parse(formData.dateOfBirth, 'yyyy-MM-dd', new Date());
                      return isValid(parsedDate) ? parsedDate : undefined;
                    })() : undefined}
                    onValueChange={(date) => {
                      if (date && isValid(date)) {
                        updateFormData({ dateOfBirth: format(date, 'yyyy-MM-dd') });
                      } else {
                        updateFormData({ dateOfBirth: '' });
                      }
                    }}
                    placeholder="MM/DD/YYYY"
                    maxDate={new Date()}
                    className="w-full rounded-lg"
                    inputStyle={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#E6E9F0',
                    }}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Height
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        min={0}
                        max={9}
                        value={formData.heightFeet ?? ''}
                        onChange={e => updateFormData({ heightFeet: e.target.value ? parseInt(e.target.value, 10) : null })}
                        className="w-full px-4 py-3 rounded-lg"
                        style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                        placeholder="Feet"
                      />
                      <input
                        type="number"
                        min={0}
                        max={11}
                        value={formData.heightInches ?? ''}
                        onChange={e => updateFormData({ heightInches: e.target.value ? parseInt(e.target.value, 10) : null })}
                        className="w-full px-4 py-3 rounded-lg"
                        style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                        placeholder="Inches"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Weight
                    </label>
                    <input
                      type="text"
                      value={formData.weight}
                      onChange={e => updateFormData({ weight: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg"
                      style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                      placeholder="180 lbs"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    State <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={formData.state}
                    onChange={e => updateFormData({ state: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                  >
                    <option value="">Select your state</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: ADAM Questionnaire */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                ADAM <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>Questionnaire</span>
              </h2>
              <p className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Androgen Deficiency in Aging Males screening tool
              </p>
              <div 
                className="p-4 rounded-lg mb-6"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
              >
                <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <strong style={{ color: '#3B82F6' }}>Why we ask:</strong> The ADAM questionnaire is a clinically validated screening tool 
                  for symptoms of low testosterone. A score of 4+ "yes" answers suggests potential hypogonadism.
                </p>
              </div>
              <div className="space-y-4">
                {ADAM_QUESTIONS.map((question, idx) => (
                  <div 
                    key={question.id}
                    className="p-4 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <p className="mb-3" style={{ color: '#E6E9F0' }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{idx + 1}.</span> {question.text}
                    </p>
                    <div className="flex gap-3">
                      {[true, false].map(value => (
                        <button
                          key={String(value)}
                          onClick={() => updateFormData({ adam: { ...formData.adam, [question.id]: value } })}
                          className="flex-1 py-3 rounded-lg font-medium transition-all"
                          style={{
                            background: formData.adam[question.id] === value 
                              ? value ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(255, 255, 255, 0.02)',
                            border: formData.adam[question.id] === value 
                              ? value ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)'
                              : '1px solid rgba(255, 255, 255, 0.1)',
                            color: formData.adam[question.id] === value ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          {value ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {ADAM_QUESTIONS.every(q => formData.adam[q.id] !== undefined) && (
                <div 
                  className="mt-6 p-4 rounded-lg"
                  style={{ 
                    background: calculateAdamScore() >= 4 ? 'rgba(249, 115, 22, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${calculateAdamScore() >= 4 ? 'rgba(249, 115, 22, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        background: calculateAdamScore() >= 4 ? 'rgba(249, 115, 22, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        color: calculateAdamScore() >= 4 ? '#f97316' : '#22c55e'
                      }}
                    >
                      {calculateAdamScore()}
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#E6E9F0' }}>
                        ADAM Score: {calculateAdamScore()}/10
                      </p>
                      <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        {calculateAdamScore() >= 4 
                          ? 'Positive screen - your provider will review with lab values'
                          : 'Negative screen - lower probability of hypogonadism'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: STOP-Bang */}
          {currentStep === 4 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                Sleep Apnea <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>Screening</span>
              </h2>
              <p className="mb-2" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                STOP-Bang questionnaire for sleep apnea risk
              </p>
              <div 
                className="p-4 rounded-lg mb-6"
                style={{ background: 'rgba(147, 51, 234, 0.1)', border: '1px solid rgba(147, 51, 234, 0.2)' }}
              >
                <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <strong style={{ color: '#a855f7' }}>Why we ask:</strong> Untreated sleep apnea can worsen with testosterone therapy 
                  and increases cardiovascular risk. High-risk patients may need sleep testing before TRT.
                </p>
              </div>
              <div className="space-y-4">
                {STOP_BANG_QUESTIONS.map((question, idx) => (
                  <div 
                    key={question.id}
                    className="p-4 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  >
                    <p className="mb-3" style={{ color: '#E6E9F0' }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{idx + 1}.</span> {question.text}
                    </p>
                    <div className="flex gap-3">
                      {[true, false].map(value => (
                        <button
                          key={String(value)}
                          onClick={() => updateFormData({ stopBang: { ...formData.stopBang, [question.id]: value } })}
                          className="flex-1 py-3 rounded-lg font-medium transition-all"
                          style={{
                            background: formData.stopBang[question.id] === value 
                              ? value ? 'rgba(147, 51, 234, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(255, 255, 255, 0.02)',
                            border: formData.stopBang[question.id] === value 
                              ? value ? '1px solid rgba(147, 51, 234, 0.5)' : '1px solid rgba(255, 255, 255, 0.3)'
                              : '1px solid rgba(255, 255, 255, 0.1)',
                            color: formData.stopBang[question.id] === value ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          {value ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {STOP_BANG_QUESTIONS.every(q => formData.stopBang[q.id] !== undefined) && (
                <div 
                  className="mt-6 p-4 rounded-lg"
                  style={{ 
                    background: calculateStopBangScore() >= 5 ? 'rgba(239, 68, 68, 0.1)' : calculateStopBangScore() >= 3 ? 'rgba(249, 115, 22, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${calculateStopBangScore() >= 5 ? 'rgba(239, 68, 68, 0.3)' : calculateStopBangScore() >= 3 ? 'rgba(249, 115, 22, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        background: calculateStopBangScore() >= 5 ? 'rgba(239, 68, 68, 0.2)' : calculateStopBangScore() >= 3 ? 'rgba(249, 115, 22, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        color: calculateStopBangScore() >= 5 ? '#ef4444' : calculateStopBangScore() >= 3 ? '#f97316' : '#22c55e'
                      }}
                    >
                      {calculateStopBangScore()}
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#E6E9F0' }}>
                        STOP-Bang Score: {calculateStopBangScore()}/8
                      </p>
                      <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        {calculateStopBangScore() >= 5 
                          ? 'High risk for OSA - sleep testing may be recommended'
                          : calculateStopBangScore() >= 3
                          ? 'Intermediate risk - your provider will evaluate further'
                          : 'Low risk for obstructive sleep apnea'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Medical History */}
          {currentStep === 5 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                Medical <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>History</span>
              </h2>
              <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Help us understand your health background
              </p>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Current Medications
                  </label>
                  <textarea
                    value={formData.currentMedications}
                    onChange={e => updateFormData({ currentMedications: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg min-h-[100px]"
                    style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                    placeholder="List any medications, supplements, or vitamins you currently take"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Allergies
                  </label>
                  <AllergyMultiSelect
                    dark
                    placeholder="Search and select allergies..."
                    value={formData.allergies}
                    onChange={(next) => updateFormData({ allergies: next })}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Have you used testosterone therapy before?
                  </label>
                  <div className="flex gap-3">
                    {['Yes', 'No'].map(option => (
                      <button
                        key={option}
                        onClick={() => updateFormData({ previousTRT: option })}
                        className="flex-1 py-3 rounded-lg font-medium transition-all"
                        style={{
                          background: formData.previousTRT === option ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.previousTRT === option ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.previousTRT === option ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.previousTRT === 'Yes' && (
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Previous TRT Details
                    </label>
                    <textarea
                      value={formData.previousTRTDetails}
                      onChange={e => updateFormData({ previousTRTDetails: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg min-h-[80px]"
                      style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#E6E9F0' }}
                      placeholder="What type, dosage, and for how long?"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Do you have any of these conditions?
                  </label>
                  <MedicalHistoryMultiSelect
                    dark
                    placeholder="Select medical conditions..."
                    value={{
                      conditions: formData.chronicConditions,
                      other: formData.chronicConditionsOther,
                    }}
                    onChange={(next) => updateFormData({
                      chronicConditions: next.conditions,
                      chronicConditionsOther: next.other,
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Family History (select all that apply)
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {FAMILY_HISTORY_OPTIONS.map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          const noneOption = 'None of the above';
                          const isNoneOption = option === noneOption;
                          const isSelected = formData.familyHistory.includes(option);
                          let newHistory: string[];

                          if (isSelected) {
                            newHistory = formData.familyHistory.filter(h => h !== option);
                          } else if (isNoneOption) {
                            // "None of the above" is mutually exclusive with all other options.
                            newHistory = [noneOption];
                          } else {
                            // Selecting a real family history option removes "None of the above".
                            newHistory = [
                              ...formData.familyHistory.filter(h => h !== noneOption),
                              option,
                            ];
                          }

                          updateFormData({ familyHistory: newHistory });
                        }}
                        className="p-3 rounded-lg text-left text-sm transition-all flex items-center gap-2"
                        style={{
                          background: formData.familyHistory.includes(option) ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.familyHistory.includes(option) ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.familyHistory.includes(option) ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {formData.familyHistory.includes(option) && <CheckCircle className="w-4 h-4" style={{ color: '#3B82F6' }} />}
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Lifestyle */}
          {currentStep === 6 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                Lifestyle & <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>Habits</span>
              </h2>
              <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Your daily habits impact hormone health
              </p>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Activity Level <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active'].map(level => (
                      <button
                        key={level}
                        onClick={() => updateFormData({ activityLevel: level })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.activityLevel === level ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.activityLevel === level ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.activityLevel === level ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Exercise Frequency
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['Never', '1-2x/week', '3-4x/week', '5+x/week'].map(freq => (
                      <button
                        key={freq}
                        onClick={() => updateFormData({ exerciseFrequency: freq })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.exerciseFrequency === freq ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.exerciseFrequency === freq ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.exerciseFrequency === freq ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {freq}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Sleep Quality <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['Poor', 'Fair', 'Good', 'Excellent'].map(quality => (
                      <button
                        key={quality}
                        onClick={() => updateFormData({ sleepQuality: quality })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.sleepQuality === quality ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.sleepQuality === quality ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.sleepQuality === quality ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {quality}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Stress Level
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['Low', 'Moderate', 'High', 'Very High'].map(level => (
                      <button
                        key={level}
                        onClick={() => updateFormData({ stressLevel: level })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.stressLevel === level ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.stressLevel === level ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.stressLevel === level ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Alcohol Consumption
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['None', 'Occasional', 'Moderate', 'Heavy'].map(level => (
                      <button
                        key={level}
                        onClick={() => updateFormData({ alcoholConsumption: level })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.alcoholConsumption === level ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.alcoholConsumption === level ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.alcoholConsumption === level ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Tobacco Use
                  </label>
                  <div className="grid md:grid-cols-2 gap-2">
                    {['Never', 'Former', 'Current', 'Vaping/Nicotine'].map(level => (
                      <button
                        key={level}
                        onClick={() => updateFormData({ tobaccoUse: level })}
                        className="p-3 rounded-lg text-sm transition-all"
                        style={{
                          background: formData.tobaccoUse === level ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                          border: formData.tobaccoUse === level ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                          color: formData.tobaccoUse === level ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Shipping & Billing Address */}
          {currentStep === 7 && (
            <ShippingBillingAddressStep
              shippingAddress={{
                addressLine1: formData.shippingAddressLine1,
                addressLine2: formData.shippingAddressLine2,
                city: formData.shippingCity,
                state: formData.shippingState,
                postalCode: formData.shippingPostalCode,
              }}
              billingAddress={{
                addressLine1: formData.billingAddressLine1,
                addressLine2: formData.billingAddressLine2,
                city: formData.billingCity,
                state: formData.billingState,
                postalCode: formData.billingPostalCode,
              }}
              billingSameAsShipping={formData.billingSameAsShipping}
              onShippingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'shippingAddressLine1',
                  addressLine2: 'shippingAddressLine2',
                  city: 'shippingCity',
                  state: 'shippingState',
                  postalCode: 'shippingPostalCode',
                };
                updateFormData({ [fieldMap[field]]: value } as Partial<MensFormData>);
              }}
              onBillingChange={(field, value) => {
                const fieldMap: Record<string, string> = {
                  addressLine1: 'billingAddressLine1',
                  addressLine2: 'billingAddressLine2',
                  city: 'billingCity',
                  state: 'billingState',
                  postalCode: 'billingPostalCode',
                };
                updateFormData({ [fieldMap[field]]: value } as Partial<MensFormData>);
              }}
              onBillingSameToggle={(value) => updateFormData({ billingSameAsShipping: value } as Partial<MensFormData>)}
              variant="dark"
            />
          )}

          {/* Step 8: Review */}
          {currentStep === 8 && (
            <div>
              <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>
                Review & <span style={{ color: '#3B82F6', fontStyle: 'italic' }}>Consent</span>
              </h2>
              <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Please review your responses and provide consent
              </p>

              {/* Summary */}
              <div 
                className="rounded-xl p-6 mb-6"
                style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              >
                <h4 className="font-semibold mb-4" style={{ color: '#E6E9F0' }}>Summary</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Name</span>
                    <span style={{ color: '#fff' }}>{formData.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Goals</span>
                    <span style={{ color: '#fff' }}>{formData.selectedGoals.length} selected</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>ADAM Score</span>
                    <span style={{ color: calculateAdamScore() >= 4 ? '#f97316' : '#22c55e' }}>
                      {calculateAdamScore()}/10 {calculateAdamScore() >= 4 ? '(Positive)' : '(Negative)'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>STOP-Bang Score</span>
                    <span style={{ color: calculateStopBangScore() >= 5 ? '#ef4444' : calculateStopBangScore() >= 3 ? '#f97316' : '#22c55e' }}>
                      {calculateStopBangScore()}/8 
                      {calculateStopBangScore() >= 5 ? ' (High Risk)' : calculateStopBangScore() >= 3 ? ' (Intermediate)' : ' (Low Risk)'}
                    </span>
                  </div>
                </div>
              </div>

              {showPayment && (
                <p className="mb-4 text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  You&apos;ll authorize your card on the next step. No charge until your prescription is approved.
                </p>
              )}
              <div className="space-y-4">
                {[
                  { id: 'consentAccurate', label: 'I confirm that all information provided is accurate to the best of my knowledge.' },
                  { id: 'consentMonitoring', label: 'I understand that testosterone therapy requires regular monitoring and follow-up visits.' },
                  { id: 'consentTerms', label: <>I have read and agree to the <a href="https://www.nimbus-os.com/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2FFFC7' }} onClick={(e) => e.stopPropagation()}>terms of service</a> and <a href="https://www.nimbus-os.com/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2FFFC7' }} onClick={(e) => e.stopPropagation()}>privacy policy</a>.</> },
                ].map(consent => (
                  <button
                    key={consent.id}
                    onClick={() => updateFormData({ [consent.id]: !formData[consent.id as keyof MensFormData] } as Partial<MensFormData>)}
                    className="w-full p-4 rounded-lg text-left flex items-start gap-3 transition-all"
                    style={{
                      background: formData[consent.id as keyof MensFormData] ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                      border: formData[consent.id as keyof MensFormData] ? '1px solid rgba(47, 255, 199, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div 
                      className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ 
                        background: formData[consent.id as keyof MensFormData] ? '#2FFFC7' : 'rgba(255, 255, 255, 0.1)',
                        border: formData[consent.id as keyof MensFormData] ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      {formData[consent.id as keyof MensFormData] && <CheckCircle className="w-3 h-3" style={{ color: '#000' }} />}
                    </div>
                    <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{consent.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === PAYMENT_STEP && showPayment && token && (
            <PaymentStep
              token={token}
              isGuided={isGuided}
              paymentStatus={paymentStatus}
              therapyLabel="TRT"
              isSubmitting={isSubmitting}
              onPaymentConfirmed={({ paymentMethodId, setupIntentId }) =>
                handleSubmit({ paymentMethodId, setupIntentId })
              }
              onSkip={() => handleSubmit()}
              onBack={() => setCurrentStep(showBooking ? BOOKING_STEP : REVIEW_STEP)}
            />
          )}

          {currentStep === BOOKING_STEP && showBooking && token && (
            <IntakeBookingStep
              token={token}
              initialSlot={selectedSlot}
              onSlotSelected={(slot) => {
                setSelectedSlot(slot);
                setCurrentStep(PAYMENT_STEP);
              }}
              onBack={() => setCurrentStep(REVIEW_STEP)}
            />
          )}

          {/* Submission Error */}
          {submissionError && (
            <div className="mt-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              <p className="font-medium">Submission Error</p>
              <p>{submissionError}</p>
            </div>
          )}

          {/* Nav is hidden on Payment and Booking steps — they render their own controls. */}
          {currentStep !== PAYMENT_STEP && currentStep !== BOOKING_STEP && (
            <div className="flex justify-between mt-8 pt-6" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all disabled:cursor-not-allowed ${
                  currentStep === 1 ? 'bg-slate-700 text-slate-500 border border-slate-600' : ''
                }`}
                style={currentStep !== 1 ? { background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', color: '#fff' } : undefined}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              {currentStep < REVIEW_STEP ? (
                <button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed ${
                    !canProceed() ? 'bg-slate-600 text-slate-400 border border-slate-500' : ''
                  }`}
                  style={canProceed() ? { background: 'linear-gradient(90deg, #3B82F6, #2FFFC7)', color: '#000' } : undefined}
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : showBooking ? (
                <button
                  onClick={() => setCurrentStep(BOOKING_STEP)}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed ${
                    !canProceed() ? 'bg-slate-600 text-slate-400 border border-slate-500' : ''
                  }`}
                  style={canProceed() ? { background: 'linear-gradient(90deg, #3B82F6, #2FFFC7)', color: '#000' } : undefined}
                >
                  Book Appointment
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : showPayment ? (
                <button
                  onClick={() => setCurrentStep(PAYMENT_STEP)}
                  disabled={!canProceed()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed ${
                    !canProceed() ? 'bg-slate-600 text-slate-400 border border-slate-500' : ''
                  }`}
                  style={canProceed() ? { background: 'linear-gradient(90deg, #3B82F6, #2FFFC7)', color: '#000' } : undefined}
                >
                  Continue to payment
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => handleSubmit()}
                  disabled={!canProceed() || isSubmitting}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed ${
                    !canProceed() || isSubmitting ? 'bg-slate-600 text-slate-400 border border-slate-500' : ''
                  }`}
                  style={canProceed() && !isSubmitting ? { background: 'linear-gradient(90deg, #2FFFC7, #3B82F6)', color: '#000' } : undefined}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit Intake
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Auto-save notice */}
          <p className="text-center mt-4 text-xs" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            Your progress is automatically saved
          </p>
        </div>
      </main>
    </div>
  );
};

export default MensIntakeLanding;
